import React, { useRef } from 'react';
import { View, TextInput, Text } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import TopPanel from '@components/operatorPanel/components/OperatorPanelInformation/TopPanel';
import { scaleOP } from '@components/frame';
import { WithColorProps } from '@styles/colors/types';
import { fontOP } from '@styles/fonts';
import { searchDisplayMode, LEVELS } from '@helpers';
import Loading from '@components/loading';
import { SearchInputWithModeSelector } from '@components/search/components/SearchInputWithModeSelector';
import Svg from '@styles/icons/svg';
import Selectors from '@styles/selectors';
import { useAllBatches } from './hooks';
import { ProductionBatch } from '@interfaces/search';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { Perspective } from '@v2/pages/common/enums';
import OperatorPanelBatchTile from './OperatorPanelBatchTile';
import { useOPDisplayMode } from '@components/operatorPanel/components/OperatorPanelInformation/components/OperatorPanelInformationSearchNCFilesScreen/hooks/useOPDisplayMode';
import { getBatchDataGridColumns } from './columns';
import { useMachineFilter } from '@components/operatorPanel/hooks/useMachineFilter';
import DataGrid from '@components/common/DataGrid';
import { TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';

const OperatorPanelInformationSearchHistoryScreen = ({
  C,
}: Pick<WithColorProps, 'C'>) => {
  const searchRef = useRef<TextInput>(null);
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const {
    displayMode,
    setDisplayMode,
    isSmallMode,
    isLineMode,
    isTableMode,
    effectiveWidth,
    columns,
    itemSize,
  } = useOPDisplayMode();

  const {
    selectedFilter,
    setSelectedFilter,
    machineFilter,
    filterOptions,
    isFilterReady,
    isEmptyFilter,
  } = useMachineFilter();

  const {
    searchText,
    setSearchText,
    batches,
    totalCount,
    loading,
    hasMore,
    handleSearch,
    clearSearch,
    loadMore,
    gridSearch,
  } = useAllBatches({ machineFilter, isFilterReady, isEmptyFilter });

  const theme = C.scheme === 'light' ? 'Light' : 'Dark';
  const bg = C(`Background/${theme}/System [mobile]`, false);

  const isInfoTilesMode = displayMode === searchDisplayMode.infoTiles;

  const goToDetails = (item: ProductionBatch) => {
    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      params: {
        perspective: Perspective.BATCH,
        batchNumber: item.batchNumber,
        batchId: item.productionBatchId,
        ncFileStatus: item.ncFileStatus,
      },
    });
  };

  const getDisplayMode = (): 'line' | 'block' | 'square' | 'infoTiles' => {
    if (isLineMode) return 'line';
    if (isSmallMode) return 'square';
    if (isInfoTilesMode) return 'infoTiles';
    return 'block';
  };

  const dataGridColumns = getBatchDataGridColumns({ scale: scaleOP, goToDetails });

  const renderBatchTile = ({ item, index }: { item: ProductionBatch; index: number }) => {
    const columnIndex = index % columns;
    const marginRight = !isLineMode && !isInfoTilesMode && columns > 1 && columnIndex < columns - 1 ? scaleOP(isSmallMode ? 8 : 18) : 0;
    const marginBottom = !isLineMode && !isInfoTilesMode ? scaleOP(isSmallMode ? 8 : 18) : 0;

    return (
      <View style={{ marginRight, marginBottom }}>
        <OperatorPanelBatchTile
          item={item}
          displayMode={getDisplayMode()}
          itemSize={itemSize}
          effectiveWidth={effectiveWidth}
          onPress={goToDetails}
          C={C}
        />
      </View>
    );
  };

  const renderHeader = () => (
    <View style={{ marginBottom: scaleOP(16) }}>
      <SearchInputWithModeSelector
        C={C}
        showModeSelector={true}
        displayMode={displayMode}
        setSelectedDisplayMode={setDisplayMode}
        level={LEVELS.ProductionBatches}
        searchRef={searchRef}
        searchValue={searchText}
        onSearch={setSearchText}
        submit={handleSearch}
        cancelAnimation={clearSearch}
        bg="Background/Light/Search & View Selector"
        scale={scaleOP}
        font={fontOP}
      />
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: scaleOP(12) }}>
        <Text style={[fontOP['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>
          {totalCount.toLocaleString()} {totalCount === 1 ? i18n.t('Batch') : i18n.t('Batches')}
        </Text>
      </View>
    </View>
  );

  const filterTabs = (
    <View style={{ flexDirection: 'row', gap: scaleOP(8) }}>
      {filterOptions.map((option) => (
        <Selectors
          key={option.id}
          variant="L"
          text={option.name}
          selected={selectedFilter === option.id}
          onPress={() => setSelectedFilter(option.id)}
          scale={scaleOP}
          font={fontOP}
        />
      ))}
    </View>
  );

  return (
    <View testID={TEST_IDS.operatorPanel.information.searchHistory.root} style={{ backgroundColor: bg, flex: 1 }}>
      <TopPanel title="NC File History" withBottomBorder submit={() => {}} centerContent={filterTabs} />
      <View style={{ paddingHorizontal: scaleOP(18), flex: 1, paddingTop: scaleOP(16) }}>
        {renderHeader()}
        {loading && batches.length === 0 ? (
          <Loading background={bg} />
        ) : batches.length === 0 ? (
          <View style={{ alignItems: 'center', marginTop: scaleOP(48) }}>
            <Svg icon="history" width={scaleOP(48)} height={scaleOP(48)} color={C('Label/Light/Tertiary')} />
            <Text style={[fontOP['Body'], { color: C('Label/Light/Tertiary'), marginTop: scaleOP(16) }]}>
              {searchText ? i18n.t('No results found') : i18n.t('No Batches found')}
            </Text>
          </View>
        ) : isTableMode ? (
          <View style={{ flex: 1, width: effectiveWidth }}>
            <DataGrid
              columns={dataGridColumns}
              data={batches}
              primaryKey="batchNumber"
              onRowClick={goToDetails}
              idName="productionBatchId"
              tableName={TABLE_NAMES.operatorPanelSearchHistory}
              loaded={!loading}
              isError={false}
              density={displayMode}
              scale={scaleOP}
              search={gridSearch}
              onRowsScrollEnd={hasMore ? loadMore : undefined}
            />
          </View>
        ) : (
          <FlashList
            data={batches}
            renderItem={renderBatchTile}
            keyExtractor={(item, index) => item?.productionBatchId?.toString() || index.toString()}
            numColumns={isLineMode || isInfoTilesMode ? 1 : columns}
            key={`${columns}-${displayMode}`}
            contentContainerStyle={{ paddingBottom: scaleOP(24) }}
            showsVerticalScrollIndicator={false}
            onEndReached={hasMore ? loadMore : undefined}
            onEndReachedThreshold={0.5}
          />
        )}
      </View>
    </View>
  );
};

export default connect(null, {})(OperatorPanelInformationSearchHistoryScreen);
